import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../state/reducer'
import { Token } from './types'
import { useTokenData } from './useTokenData'
import { TokenStatsCards } from './TokenStatsCards'
import { TokenLocationConfirm } from './TokenLocationConfirm'
import TokenDisplay from './TokenDisplay'
import NoTokenLocationSelected from '../NoTokenLocationSelected'
import { Card } from 'react-bootstrap'
import { updateApplicationKey } from '../../../state/application'
import { SocketDisplay } from '../../../Layouts'

export const TokenDisplayPage: React.FC = () => {
  const dispatch = useDispatch()
  const [confirmed, setConfirmed] = useState(false)

  const geoLocation = useSelector(
    (state: RootState) => state.application.geoLocation
  )
  const organization = useSelector(
    (state: RootState) => state.machineConfig.organization
  )
  const { displayTokens, currentDisplayToken, avgServiceTime } = useSelector(
    (state: RootState) => state.tokens
  )

  useEffect(() => {
    // On mount: show header
    dispatch(updateApplicationKey('showHeader', false))

    // On unmount: hide header
    return () => {
      dispatch(updateApplicationKey('showHeader', true))
    }
  }, [dispatch])

  useTokenData(displayTokens, organization, geoLocation)

  let content: React.ReactNode
  if (!organization) {
    content = <NoTokenLocationSelected />
  } else if (!confirmed) {
    content = (
      <TokenLocationConfirm
        organization={organization}
        onConfirm={() => setConfirmed(true)}
      />
    )
  } else {
    const waiting =
      displayTokens?.filter((x: Token) => !x.counter_number).length || 0
    const avgWait = calculateAvgWait(displayTokens || [])
    const avgService = avgServiceTime ? parseInt(avgServiceTime) : undefined

    content = (
      <div className={'p-3'}>
        <TokenStatsCards
          waiting={waiting}
          avgWait={avgWait}
          avgService={avgService}
        />

        <TokenDisplay
          currentDisplayToken={currentDisplayToken}
          tokens={displayTokens}
        />

        <Card
          className="mt-4  text-white border-0"
          style={{ backgroundColor: '#1e293b' }}
        >
          <Card.Body>
            <h5>These Tokens are valid only for</h5>
            <p className="mb-0">
              {organization.name}, {organization.address}, {organization.city},{' '}
              {organization.state}
            </p>
          </Card.Body>
        </Card>
      </div>
    )
  }

  // Mount the realtime socket once, as a stable first child present on every
  // return path, so it is NOT gated by `organization`/`confirmed`. Switching
  // between the no-location / confirm / full-display branches must not
  // unmount/remount SocketDisplay (that toggling is what caused the Bug 1
  // reconnect storm). SocketDisplay's own internal gate still defers opening
  // the socket until organization + machine_id are available.
  return (
    <>
      <SocketDisplay />
      {content}
    </>
  )
}

function calculateAvgWait(tokens: Token[]): number {
  let total = 0,
    count = 0
  for (const t of tokens) {
    if (!t.invite_sent_on || t.has_customer_left) continue
    const wait =
      (new Date(t.invite_sent_on).getTime() -
        new Date(t.created_at).getTime()) /
      60000
    total += wait
    count++
  }
  return count ? Math.floor(total / count) : 0
}
